/**
 * services/web-ui/src/components/audit/AnomalyBanner.tsx
 *
 * Banner shown at the top of CallDetail.
 * Green success badge when all rules satisfied; red expandable list when not.
 */

import { useState } from "react";
import type { SessionAnomaly } from "../../api/voice-sessions";

interface Props {
  anomalies: SessionAnomaly[];
  allRulesSatisfied: boolean;
}

export function AnomalyBanner({ anomalies, allRulesSatisfied }: Props) {
  const [expanded, setExpanded] = useState(false);

  if (allRulesSatisfied) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-green-900/30 border border-green-800/50 rounded-lg">
        <span className="text-green-400 text-sm">&#10003;</span>
        <span className="text-green-300 text-sm font-medium">All compliance rules satisfied</span>
      </div>
    );
  }

  return (
    <div className="bg-red-950/40 border-l-4 border-red-500 rounded-r-lg px-4 py-3">
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center gap-2">
          <span className="text-red-400 text-sm font-bold">&#9888;</span>
          <span className="text-red-300 text-sm font-semibold">
            {anomalies.length} anomal{anomalies.length === 1 ? "y" : "ies"} detected
          </span>
        </div>
        <button className="text-red-400 text-xs hover:text-red-200 transition-colors">
          {expanded ? "Hide" : "Show details"}
        </button>
      </div>

      {expanded && (
        <ul className="mt-3 space-y-1.5 pl-4 border-l border-red-800/50">
          {anomalies.map((a, i) => (
            <li key={i} className="text-sm">
              <span className="text-red-400 font-mono text-xs mr-2">{a.type}</span>
              <span className="text-red-200">{a.message}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
